import {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";
import { IRoute } from "./routes";
import { Button } from "@/components/ui/button";
import { useHistory, useLocation } from "react-router-dom";
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter
} from "@/components/ui/alert-dialog";

export interface IBlockedRoute {
    route: IRoute;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    params: any;
    locationSearch: string;
    key: string;
}

interface IUnsavedContext {
    blockedRoute: IBlockedRoute | undefined;
    historyStateId: string | undefined;
    states: string[];
    setBlockedRoute: (blockedRoute: IBlockedRoute | undefined) => void;
    handleCancel: () => void;
}

const unsavedContext = createContext<IUnsavedContext>({
    blockedRoute: undefined,
    historyStateId: undefined,
    states: [],
    setBlockedRoute: () => undefined,
    handleCancel: () => undefined
});

const currentRouteContext = createContext<IBlockedRoute | undefined>(undefined);

export const CurrentRouteProvider = ({
    children,
    originalRoute
}: {
    children: ReactNode;
    originalRoute: IBlockedRoute;
}) => {
    const thisRoute = useUnsavedBlockedRoute(originalRoute);
    const showUnsavedWarningDialog = thisRoute.route !== originalRoute.route;

    // need a stable route object otherwise an setBlockedRoute gets called on every render even if the route hasn't actually changed (caused by originalRoute being a new object on each Page render)
    const memoedRoute = useMemoedBlockedRoute(thisRoute);

    return (
        <>
            <currentRouteContext.Provider value={memoedRoute}>
                {children}
            </currentRouteContext.Provider>
            {showUnsavedWarningDialog && <UnsavedWarningDialog />}
        </>
    );
};

export const UnsavedChangesProvider = ({
    children
}: {
    children: ReactNode;
}) => {
    const [blockedRoute, rawSetBlockedRoute] = useState<
        IBlockedRoute | undefined
    >(undefined);
    const [historyStateId, setHistoryStateId] = useState<string | undefined>(
        undefined
    );

    // need a stable blockedRoute object otherwise an infinite loop occurs (because originalRoute is constructed each time in Page render)
    const memoedBlockedRoute = useMemoedBlockedRoute(blockedRoute);

    const setBlockedRoute = (newBlockedRoute: IBlockedRoute | undefined) => {
        rawSetBlockedRoute(newBlockedRoute);
        if (newBlockedRoute) {
            const historyId = getHistoryId();
            setHistoryStateId(historyId);
        } else {
            setHistoryStateId(undefined);
        }
    };

    const states = useHistoryStates();
    const history = useHistory();

    const handleCancel = () => {
        if (memoedBlockedRoute && historyStateId) {
            if (
                states.length > 1 &&
                states[states.length - 2] === historyStateId
            ) {
                // target page is one back in history
                window.history.back();
            } else {
                // assume we need to go forward if target page is not one back in history
                window.history.forward();
            }

            // last resort
            setTimeout(() => {
                if (getHistoryId() !== historyStateId) {
                    history.push(historyStateId);
                }
            }, 400);
        }
    };

    return (
        <unsavedContext.Provider
            value={{
                blockedRoute: memoedBlockedRoute,
                historyStateId,
                states,
                setBlockedRoute,
                handleCancel
            }}
        >
            {children}
        </unsavedContext.Provider>
    );
};

// eslint-disable-next-line react-compiler/react-compiler
const useMemoedBlockedRoute = (
    blockedRoute: IBlockedRoute | undefined
): IBlockedRoute | undefined => {
    const hasBlockedRoute = !!blockedRoute;
    const memoedBlockedRoute = useMemo(
        // eslint-disable-next-line react-compiler/react-compiler
        (): IBlockedRoute | undefined => {
            return hasBlockedRoute
                ? {
                      route: blockedRoute.route,
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                      params: blockedRoute.params,
                      locationSearch: blockedRoute.locationSearch,
                      key: blockedRoute.key
                  }
                : undefined;
        },
        [
            hasBlockedRoute,
            blockedRoute?.route,
            blockedRoute?.key,
            blockedRoute?.locationSearch,
            blockedRoute?.params
        ]
    );
    return memoedBlockedRoute;
};

const getHistoryId = () => {
    return window.location.hash.substring(1);
};

const useLocationHistoryId = () => {
    const location = useLocation();
    return location.pathname + location.search;
};

const useHistoryStates = () => {
    const historyId = useLocationHistoryId();

    const [states, setStates] = useState<string[]>([]);
    useEffect(() => {
        setStates(prev => {
            if (prev.length > 1 && prev[prev.length - 2] === historyId) {
                // we've gone backwards 1 page
                const value = prev.slice(0, prev.length - 1);
                return value;
            }
            const value = [...prev, historyId];
            return value;
        });
    }, [historyId]);

    return states;
};

const UnsavedWarningDialog = () => {
    const { setBlockedRoute, handleCancel, states } =
        useContext(unsavedContext);

    const historyId = useLocationHistoryId();
    const [initialHistoryState] = useState(historyId);
    const doubleEffectFix = useRef<string | undefined>();

    useEffect(() => {
        if (historyId !== initialHistoryState) {
            if (doubleEffectFix.current === historyId) {
                // protect against double effect
                return;
            }
            doubleEffectFix.current = historyId;
            setTimeout(() => {
                if (doubleEffectFix.current === historyId) {
                    doubleEffectFix.current = undefined;
                }
            }, 400);

            if (
                states.length > 1 &&
                states[states.length - 1] === initialHistoryState
            ) {
                // target page is last in history
                window.history.back();
            } else {
                // assume we need to go forward if target page is not last in history
                window.history.forward();
            }
        }
    }, [historyId, initialHistoryState, states]);

    return (
        <AlertDialog open={true}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
                </AlertDialogHeader>
                <AlertDialogDescription>
                    You have unsaved changes. Are you sure you want to leave
                    this page?
                </AlertDialogDescription>
                <AlertDialogFooter>
                    <div className="flex gap-2">
                        <Button onClick={handleCancel}>Cancel</Button>
                        <Button
                            variant="destructive"
                            onClick={() => {
                                setBlockedRoute(undefined);
                            }}
                        >
                            Leave page
                        </Button>
                    </div>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};

const useBeforeUnload = (shouldPrevent: boolean) => {
    useEffect(() => {
        if (!shouldPrevent) {
            return;
        }

        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
        };

        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [shouldPrevent]);
};

export const useUnsavedBlockedRoute = (
    originalRoute: IBlockedRoute
): IBlockedRoute => {
    const context = useContext(unsavedContext);
    return context.blockedRoute &&
        context.blockedRoute.route !== originalRoute.route
        ? context.blockedRoute
        : originalRoute;
};

export const useUnsavedChanges = (hasChanges: boolean) => {
    const { setBlockedRoute } = useContext(unsavedContext);
    const currentRoute = useContext(currentRouteContext);
    useEffect(() => {
        setBlockedRoute(hasChanges && currentRoute ? currentRoute : undefined);
    }, [currentRoute, hasChanges, setBlockedRoute]);

    useBeforeUnload(hasChanges);

    return () => {
        setBlockedRoute(undefined);
    };
};
